import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
  Typography,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useAppSelector } from '../../../../../hooks';
import {
  languageOptions,
  proficiencyOptions,
  TP_COLORS,
} from '../../../../../constants';

import { getAuthState } from '../../../../../store/AuthSlice';
import { IUserLanguage } from '../../../../../store/UserSlice';
import {
  addUserLanguage,
  updateUserLanguage,
} from '../../../../../store/UserSlice/UserApi';

import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import { addButtonProps, dismissButtonProps } from '..';
import {
  dialogTitleProps,
  formControlProps,
  textFieldProps,
} from './AddExperienceModal';

const toggleStyles: ToggleButtonProps['sx'] = {
  sx: {
    margin: '10px',
    padding: '5px 20px',
    borderRadius: '26px !important',
    border: '1px solid #CBD5E1 !important',
    '&.Mui-selected': {
      backgroundColor: TP_COLORS.gray.darken,
      color: '#F8FAFC',
      '&:hover': {
        backgroundColor: TP_COLORS.gray.light,
      },
    },
  },
};

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  language: TSelectOption | null;
  proficiency: string | null;
};

const initialState: TState = {
  language: null,
  proficiency: null,
};

interface AddLanguageModalProps {
  open: boolean;
  selectedLanguageItem?: IUserLanguage;
  onClose: () => void;
}

const AddLanguageModal: FunctionComponent<AddLanguageModalProps> = ({
  open,
  selectedLanguageItem,
  onClose,
}) => {
  const dispatch = useDispatch();
  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit'>('add');

  const isSubmitDisabled =
    !state.language || !state.proficiency || authState.status === 'loading';

  useEffect(() => {
    if (selectedLanguageItem) {
      setMode('edit');
      setState({
        language: {
          value: selectedLanguageItem.language,
          label: selectedLanguageItem.language,
        },
        proficiency: selectedLanguageItem.proficiency,
      });
    }
  }, []);

  const handleLanguageChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      language: value,
    }));
  };

  const handleProficiencyChange = (
    event: React.MouseEvent<HTMLElement>,
    value: string | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      proficiency: value,
    }));
  };

  const handleSubmit = async () => {
    if (state.language?.value && state.proficiency) {
      setLoading(true);
      const apiData = {
        language: state.language.value,
        proficiency: state.proficiency,
      };
      if (mode === 'edit' && selectedLanguageItem) {
        await dispatch(
          updateUserLanguage({
            ...apiData,
            _id: selectedLanguageItem._id,
          })
        );
      } else {
        await dispatch(addUserLanguage(apiData));
      }
      setLoading(false);
    }
    onClose();
  };

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { padding: '30px', borderRadius: '20px' } }}
    >
      <DialogTitle {...dialogTitleProps}>
        {mode === 'edit' ? 'Update' : 'Add'} Language
      </DialogTitle>
      <DialogContentText fontSize="12px">
        Specify the languages you are proficient in.
      </DialogContentText>
      <DialogContent sx={{ padding: '30px 0px', maxWidth: '380px' }}>
        <FormControl {...formControlProps}>
          <Autocomplete
            size="small"
            value={state.language}
            options={languageOptions}
            onChange={handleLanguageChange}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            popupIcon={TPAutoCompleteArrowIconComponent}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Language*"
                placeholder="Language"
              />
            )}
          />
        </FormControl>

        <Typography variant="subtitle2">Proficiency *</Typography>
        <ToggleButtonGroup
          color="standard"
          value={state.proficiency}
          exclusive
          onChange={handleProficiencyChange}
        >
          {proficiencyOptions.map((proficiencyItem, index) => (
            <ToggleButton
              key={index}
              {...toggleStyles}
              value={proficiencyItem.value}
            >
              {proficiencyItem.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </DialogContent>
      <DialogActions>
        <Button {...dismissButtonProps} onClick={onClose}>
          Dismiss
        </Button>
        <LoadingButton
          {...addButtonProps}
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddLanguageModal;
